import {
  type MouseEvent,
  type MouseEventHandler,
  useCallback,
  useMemo,
} from 'react';

// Generic type for the interactive element
type ClickableElement =
  | HTMLButtonElement
  | HTMLAnchorElement
  | HTMLDivElement
  | HTMLElement;

// Hook to conditionally handle click events based on a disabled state
// Returns a stable click handler function or undefined if disabled or no callback is provided
export const useConditionalClickHandler = <T extends ClickableElement>(
  disabled: boolean | undefined,
  onClickCallback: MouseEventHandler<T> | undefined,
): MouseEventHandler<T> | undefined => {
  // Create a stable handler function using useCallback
  const handleClick = useCallback(
    (event: MouseEvent<T>) => {
      if (!onClickCallback) {
        return;
      }

      onClickCallback(event);
    },
    [onClickCallback],
  );

  return useMemo(() => {
    if (disabled || !onClickCallback) {
      return undefined;
    }
    return handleClick;
  }, [disabled, onClickCallback, handleClick]);
};
